import { useEffect, useMemo, useRef, useState } from "react";
import { SESSION_THOUGHT_PROCESS_TEXTS } from "./reasoning-group.constants";

function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T>();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
}

/**
 * Coalesces text changes:
 * - waits `minDisplayMs` before swapping
 * - holds a lock for `animationMs` after swap (to avoid flicker)
 * - if locked, only applies updates when `interruptDuringAnimation` is true
 */
function useTextTransition({
  targetText,
  minDisplayMs,
  animationMs,
  interruptDuringAnimation = false,
}: {
  targetText: string;
  minDisplayMs: number;
  animationMs: number;
  interruptDuringAnimation?: boolean;
}): string {
  const [displayedText, setDisplayedText] = useState(targetText);

  const swapId = useRef<number | null>(null);
  const unlockId = useRef<number | null>(null);
  const isAnimatingRef = useRef(false);

  const clearTimers = () => {
    if (swapId.current != null) {
      window.clearTimeout(swapId.current);
      swapId.current = null;
    }

    if (unlockId.current != null) {
      window.clearTimeout(unlockId.current);
      unlockId.current = null;
    }
  };

  useEffect(() => {
    const same = targetText === displayedText;
    const animationsOff = animationMs === 0;

    if (animationsOff || same) {
      clearTimers();
      isAnimatingRef.current = false;
      if (!same) setDisplayedText(targetText);
      return;
    }

    // If we’re mid-animation and this update isn’t marked as interruptible, ignore it.
    if (isAnimatingRef.current && !interruptDuringAnimation) {
      return;
    }

    clearTimers();

    // Wait before applying the swap
    swapId.current = window.setTimeout(
      () => {
        isAnimatingRef.current = true;
        setDisplayedText(targetText);

        // Hold the lock for the animation duration
        unlockId.current = window.setTimeout(() => {
          isAnimatingRef.current = false;
        }, animationMs);
      },
      Math.max(0, minDisplayMs)
    );

    return clearTimers;
  }, [targetText, minDisplayMs, animationMs, interruptDuringAnimation, displayedText]);

  // cleanup on unmount
  useEffect(() => clearTimers, []);

  return displayedText;
}

function getAnimationConfig({
  isCollapsed,
  isProcessing,
  justCollapsed,
  justFinishedProcessing,
  processingMinDisplayMs,
}: {
  isCollapsed: boolean;
  isProcessing: boolean;
  justCollapsed: boolean;
  justFinishedProcessing: boolean;
  processingMinDisplayMs?: number;
}): { minDisplayMs: number; animationMs: number } {
  if (!isCollapsed || justCollapsed) {
    return { minDisplayMs: 0, animationMs: 0 };
  }

  if (isProcessing) {
    return { minDisplayMs: processingMinDisplayMs ?? 300, animationMs: 300 };
  }

  if (justFinishedProcessing) {
    return { minDisplayMs: 100, animationMs: 100 };
  }

  return { minDisplayMs: 0, animationMs: 0 };
}

function getTargetText({
  isCollapsed,
  isProcessing,
  itemCount,
  latestThinkingText,
}: {
  isCollapsed: boolean;
  isProcessing: boolean;
  itemCount: number;
  latestThinkingText: string;
}): string {
  const { HIDE_THOUGHT_PROCESS, SHOW_THOUGHT_PROCESS } = SESSION_THOUGHT_PROCESS_TEXTS;

  if (!isCollapsed) {
    return HIDE_THOUGHT_PROCESS;
  }

  if (itemCount <= 1) {
    return latestThinkingText;
  }

  if (isProcessing) {
    return latestThinkingText;
  }

  return SHOW_THOUGHT_PROCESS;
}

export function useReasoningHeader({
  latestThinkingText,
  isProcessing,
  isCollapsed,
  itemCount,
  processingMinDisplayMs,
}: {
  latestThinkingText: string;
  isProcessing: boolean;
  isCollapsed: boolean;
  itemCount: number;
  processingMinDisplayMs?: number;
}): {
  displayedText: string;
  animationMs: number;
  shouldShowChevron: boolean;
} {
  const prevIsCollapsed = usePrevious(isCollapsed) ?? false;
  const prevIsProcessing = usePrevious(isProcessing) ?? false;

  const justCollapsed = isCollapsed && !prevIsCollapsed;
  const justFinishedProcessing = !isProcessing && prevIsProcessing;

  const { minDisplayMs, animationMs } = useMemo(
    () =>
      getAnimationConfig({
        isCollapsed,
        isProcessing,
        justCollapsed,
        justFinishedProcessing,
        processingMinDisplayMs,
      }),
    [isCollapsed, isProcessing, justCollapsed, justFinishedProcessing, processingMinDisplayMs]
  );

  const targetText = useMemo(
    () => getTargetText({ isCollapsed, isProcessing, itemCount, latestThinkingText }),
    [isCollapsed, isProcessing, itemCount, latestThinkingText]
  );

  // Only these header status texts should interrupt an in-flight animation.
  const { HIDE_THOUGHT_PROCESS, SHOW_THOUGHT_PROCESS } = SESSION_THOUGHT_PROCESS_TEXTS;
  const interruptDuringAnimation =
    targetText === HIDE_THOUGHT_PROCESS || targetText === SHOW_THOUGHT_PROCESS;

  const displayedText = useTextTransition({
    targetText,
    minDisplayMs,
    animationMs,
    interruptDuringAnimation,
  });

  const shouldShowChevron = itemCount > 1 && !isProcessing;

  return { displayedText, animationMs, shouldShowChevron };
}
